import { trackEvent } from "./track";
import isProductDetail from "./helpers/track/isProductDetail";
import getPageName from "./helpers/track/getPageName";

// Listener management per carousel/container using AbortController.
// All listeners added below are bound with a signal so we can cleanly abort them.
const listenerControllers = new WeakMap();

const getController = (container, key) => {
    if (!container) return null;
    let perContainer = listenerControllers.get(container);
    if (!perContainer) {
        perContainer = {};
        listenerControllers.set(container, perContainer);
    }
    if (!perContainer[key]) {
        perContainer[key] = new AbortController();
    }
    return perContainer[key];
};

const abortController = (container, key) => {
    const perContainer = listenerControllers.get(container);
    const ctrl = perContainer?.[key];
    ctrl?.abort();
};

const getSlideData = (slide) => {
    if (!slide) return;

    // Expanded overlay detection (Fancybox renders outside the inline container)
    const isExpanded = !!slide.closest(".fancybox__container");

    // Inline carousel: only the direct <img> child; Expanded: image inside Fancybox content
    const img = isExpanded ? slide.querySelector(".fancybox__slide .fancybox__content img") : slide.querySelector(":scope > img");
    const imagePath = img?.getAttribute("src") || "";

    // Derive imageName: prefer alt, then title, else filename from imagePath
    const imageTitle = img?.getAttribute("title")?.trim();
    const imageAlt = img?.getAttribute("alt")?.trim();
    const imageName =
        imageAlt ||
        imageTitle ||
        (() => {
            if (!imagePath) return "";
            const withoutQuery = imagePath.split("?")[0].split("#")[0];
            const segments = withoutQuery.split("/");
            try {
                return decodeURIComponent(segments[segments.length - 1] || "");
            } catch (e) {
                return segments[segments.length - 1] || "";
            }
        })();

    return { imagePath, imageName };
};

const trackActiveSlide = (carousel) => {
    if (!carousel) return;

    const activeSlide = carousel.querySelector(".f-carousel__slide.is-selected") || carousel.querySelector(".fancybox__slide.is-selected");

    if (!activeSlide) return;

    const { imageName } = getSlideData(activeSlide);

    trackEvent({
        event: "click_item_carousel",
        event_type: "click",
        ...(imageName && { banner_name: imageName }),
        ...(isProductDetail() && { item_name: getPageName() }),
    });
};

const trackSlide = (slide) => {
    if (!slide) return;

    const { imageName } = getSlideData(slide);

    trackEvent({
        event: "click_item_carousel",
        event_type: "click",
        ...(imageName && { banner_name: imageName }),
        ...(isProductDetail() && { item_name: getPageName() }),
    });
};

const trackImageCarousel = (carousel) => {
    // Guard: avoid duplicate listeners on the same inline carousel instance
    if (!carousel || carousel.container.dataset.trackingInitialized) return;
    carousel.container.dataset.trackingInitialized = "true";

    const { slides, plugins, container } = carousel;
    const inlineCtrl = getController(container, "inline");

    // Track slide info on click
    slides?.forEach((slide) => {
        slide?.el?.addEventListener("click", () => {
            trackSlide(slide.el);
        }, { signal: inlineCtrl?.signal });
    });

    const { Dots, Navigation, Thumbs } = plugins;

    // Track active slide on click of prev/next buttons
    if (Navigation) {
        [Navigation.next, Navigation.prev].forEach((btn) => {
            btn?.addEventListener("click", () => {
                setTimeout(() => trackActiveSlide(container), 0);
            }, { signal: inlineCtrl?.signal });
        });
    }

    // Track active slide on carousel dot buttons
    if (Dots?.list) {
        const dotButtons = Dots.list.querySelectorAll("li button");

        dotButtons.forEach((btn) => {
            btn?.addEventListener("click", () => {
                setTimeout(() => trackActiveSlide(container), 0);
            }, { signal: inlineCtrl?.signal });
        });
    }

    // Thumbnails: bind immediately if slides are present; otherwise wait for 'ready'
    if (Thumbs?.carousel) {
        const bindThumbs = () => {
            const thumbnailButtons = Thumbs.carousel.slides;

            thumbnailButtons.forEach((thumb) => {
                thumb.el?.addEventListener("click", () => {
                    setTimeout(() => trackActiveSlide(container), 0);
                }, { signal: inlineCtrl?.signal });
            });
        };
        
        // Bind immediately if ready, or wait for ready event
        if (Thumbs.carousel.slides?.length > 0) {
            bindThumbs();
        } else {
            Thumbs.carousel.on("ready", bindThumbs);
        }
    }
};

// Separate expanded tracking for clarity; allow future divergence from inline
const trackExpandedImageCarousel = (carousel, galleryId) => {
    // Guard: avoid duplicate listeners on the same expanded carousel instance
    if (!carousel || carousel.container.dataset.trackingInitialized) return;
    carousel.container.dataset.trackingInitialized = "true";

    carousel.on("ready", (fb) => {
        const { Navigation, Thumbs, container } = fb.plugins;
        const mainCarouselContainer = document.getElementById(galleryId) || container
        const expandedCtrl = getController(container, "expanded");

        // Track active slide on click of prev/next buttons
        if (Navigation) {
            [Navigation.next, Navigation.prev].forEach((btn) => {
                btn?.addEventListener("click", () => {
                    setTimeout(() => trackActiveSlide(mainCarouselContainer), 0);
                }, { signal: expandedCtrl?.signal });
            });
        }

        // Track active slide on carousel thumbnail buttons
        if (Thumbs?.carousel) {
            const bindThumbs = () => {
                const thumbnailButtons = Thumbs.carousel.slides;
    
                thumbnailButtons.forEach((thumb) => {
                    thumb.el?.addEventListener("click", () => {
                        setTimeout(() => trackActiveSlide(mainCarouselContainer), 0);
                    }, { signal: expandedCtrl?.signal });
                });
            };
            
            // Bind immediately if ready, or wait for ready event
            if (Thumbs.carousel.slides?.length > 0) {
                bindThumbs();
            } else {
                Thumbs.carousel.on("ready", bindThumbs);
            }
        }
    });
};

// Public cleanup helpers (do not toggle initialization flags)
const cleanupInlineImageCarousel = (container) => {
    abortController(container, "inline");
};

const cleanupExpandedImageCarousel = (container) => {
    abortController(container, "expanded");
};

export { 
    getSlideData, 
    trackActiveSlide, 
    trackSlide, 
    trackImageCarousel, 
    trackExpandedImageCarousel,
    cleanupInlineImageCarousel,
    cleanupExpandedImageCarousel
};
